import { createSelector } from '@reduxjs/toolkit';

import {
  getExtraBasicInfoQuestions,
  getTenantConfigs,
} from '@/constants/tenant-configs';

import { PEPAnswerStatus, QuestionCode } from '@/types/api/pep';
import {
  DocumentSection,
  DocumentStatus,
  SigningDocument,
} from '@/types/document';
import { UserAccountType } from '@/types/user';

import {
  getDocumentTypeStatus,
  getSignedDocuments,
  getSigningFormsAggregateStatus,
  standardizeDocumentsToSign,
} from '@/utils/document';
import { getSigningFormURL } from '@/utils/signingDocument';

import { selectLoggedInUser } from '../auth/selectors';
import { selectConfigsState } from '../configs';
import { selectTerritories } from '../countries';
import { selectSigningDocumentsState } from '../documentSigning/selectors';
import { RootState } from '../store';
import { selectUserHolderJointMinor } from '../user-data/selectors';

export const selectOperationData = createSelector(
  (state: RootState) => state.operationData,
  data => ({ ...data }),
);

export const selectOperationUser = createSelector(
  selectOperationData,
  data => data.user,
);

export const selectOperationDataInitialized = createSelector(
  selectOperationData,
  data => Object.values(data.initialized).every(initialized => initialized),
);

export const selectSameOperationIdAccountTypes = createSelector(
  selectOperationUser,
  user =>
    user?.accountTypes?.filter(
      type => type.operationId === user?.operationId,
    ) ?? [],
);

export const selectSigningDocuments = createSelector(
  [
    (state: RootState) => selectConfigsState(state),
    selectOperationData,
    selectSameOperationIdAccountTypes,
    selectTerritories,
    selectSigningDocumentsState,
    selectLoggedInUser,
    selectUserHolderJointMinor,
  ],
  (
    configData,
    operationData,
    accountTypes,
    { territoryByCountryCode },
    documentSigningState,
    loggedInUser,
    holderJointMinor,
  ) => {
    const {
      user,
      documentsBySection,
      pepQuestions,
      requiredDocumentSections,
      accountTypeById,
    } = operationData;
    // Document detail from API docSignForms response
    const { hashSigningDocsByCode } = documentSigningState;

    // Required signing documents for operation
    const requiredSigningDocs =
      requiredDocumentSections.find(
        section => section.type === DocumentSection.DocumentSign,
      )?.docs || [];

    const { occupations, directorOccupations } = configData;

    let formsToSign = requiredSigningDocs.reduce((acc, doc) => {
      if (hashSigningDocsByCode[doc]) {
        acc.push(hashSigningDocsByCode[doc]);
      }
      return acc;
    }, [] as SigningDocument[]);

    formsToSign = standardizeDocumentsToSign(
      formsToSign,
      accountTypes ?? [],
      user?.tenant ? getTenantConfigs(user.tenant).formsOnEachAccountType : [],
    );

    const userAccountTypes = (user?.accountTypes ?? []).map(
      (account): UserAccountType => {
        const accountDetails = accountTypeById[account.id];
        return accountDetails
          ? ({ ...accountDetails, ...account } as UserAccountType)
          : account;
      },
    );

    formsToSign = formsToSign.map(item => ({
      ...item,
      url: getSigningFormURL({
        docToSign: {
          ...item,
          docType: item.code,
        },
        occupations,
        directorOccupations,
        user,
        userAccountTypes,
        pepQuestions,
        territoryByCountryCode,
        rightStartData: {
          adult: loggedInUser,
          minorHolderEmail: holderJointMinor?.email ?? '',
        },
      }),
    }));

    const allSubmittedSigningDocuments =
      documentsBySection[DocumentSection.DocumentSign] || [];

    const documentsSigningStatus = getSigningFormsAggregateStatus(
      allSubmittedSigningDocuments,
      formsToSign.length,
    );

    const signedDocuments = getSignedDocuments(
      allSubmittedSigningDocuments,
      formsToSign,
    );

    return {
      formsToSign,
      documentsSigningStatus,
      signedDocuments,
      remainingDocsToSign: Math.max(
        formsToSign.length - signedDocuments.length,
        0,
      ),
    };
  },
);

export const selectLivenessStatuses = createSelector(
  selectOperationData,
  data => {
    const { documentsBySection } = data;

    const selfieStatus = getDocumentTypeStatus(
      documentsBySection[DocumentSection.Selfie] || [],
    );

    const livenessStatus = getDocumentTypeStatus(
      documentsBySection[DocumentSection.IDVideo] || [],
    );

    return {
      selfieStatus,
      livenessStatus,
    };
  },
);

export const selectPEPQuestions = createSelector(
  selectOperationData,
  data => data.pepQuestions,
);

export const selectPersonalUseQuestion = createSelector(
  selectOperationData,
  data => data.pepQuestions.find(q => q.code === QuestionCode.Personal),
);

export const selectEXCUserReviewStatus = createSelector(
  selectOperationUser,
  user => !!user?.clientReviewed,
);

export const selectBasicInfoStatus = createSelector(
  selectOperationData,
  operationData => {
    const { user: minorUser } = operationData;

    if (!minorUser) return undefined;

    const extraQuestionsLeft = Object.values(
      getExtraBasicInfoQuestions(minorUser.tenant, minorUser),
    ).filter(item => item.enabled);

    if (extraQuestionsLeft.length > 0) return undefined;

    let status: DocumentStatus | undefined = undefined;

    switch (minorUser.pepAnswerStatus) {
      case PEPAnswerStatus.Approved:
        status = DocumentStatus.Approved;
        break;
      case PEPAnswerStatus.Rejected:
        status = DocumentStatus.Rejected;
        break;
      case PEPAnswerStatus.InReview:
        status = DocumentStatus.InReview;
        break;
    }

    return status;
  },
);
